/**
 * The one WebSocket of the application.
 *
 * Owns: connecting, the challenge-response handshake, request/response
 * correlation, topic subscriptions and reconnection with backoff. Nothing else
 * in the client opens a socket, and there is no `fetch()` to our own server
 * anywhere in this directory - that is the transport rule of the project.
 */
import {
  PROTOCOL_VERSION, RECONNECT_MAX_MS, RECONNECT_MIN_MS, REQUEST_TIMEOUT_MS,
  SIGNATURE_PREFIX, SSR_COOKIE, WS_PATH,
} from '../../shared/constants.ts';
import { randomBase64Url } from '../../shared/base64.ts';
import { signaturePayload, type ServerFrame } from '../../shared/protocol/envelope.ts';
import { protocolError, type ProtocolError } from '../../shared/protocol/errors.ts';
import type { MethodName } from '../../shared/protocol/methods.ts';
import {
  activeSummary, clearEntered, clearSignedOut, currentUnlocked, hasEntered,
  isSignedOut, listKeys, lock, markEntered, markSignedOut, resumeActiveKey,
  signPayload, unlockActiveKey, type UnlockedKey,
} from '../auth/key-store.ts';
import { setState } from './store.ts';

interface Pending {
  resolve(value: unknown): void;
  reject(error: ProtocolError): void;
  timer: ReturnType<typeof setTimeout>;
}

type TopicListener = (seq: number, data: unknown) => void;

const pending = new Map<number, Pending>();
const topics = new Map<string, Set<TopicListener>>();
const lastSeq = new Map<string, number>();

let socket: WebSocket | null = null;
let key: UnlockedKey | null = null;
let nextId = 1;
let attempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let closedByUs = false;

/** Что сделать сразу после успешного входа. Ставится один раз при старте. */
let onAuthenticated: (() => void | Promise<void>) | null = null;

export function setOnAuthenticated(handler: () => void | Promise<void>): void {
  onAuthenticated = handler;
}

function wsUrl(): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}${WS_PATH}`;
}

function sendFrame(frame: unknown): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify(frame));
}

function failAllPending(error: ProtocolError): void {
  for (const [, p] of pending) {
    clearTimeout(p.timer);
    p.reject(error);
  }
  pending.clear();
}

/**
 * Works out what the page should show on load.
 *
 * Two different events must not be confused: pressing «Выход» and pressing F5.
 * A logout has to survive a reload, or it is not a logout. A reload must not
 * undo a session, or working in the console becomes a chore. So a reload
 * resumes what was already open, and only an explicit exit ends it.
 *
 * The exception is a key encrypted by the authenticator: after a reload the
 * decrypted material is genuinely gone, so entering needs one click and a
 * Hello prompt. That is the price of the protection, and it is the point.
 */
export async function bootstrap(): Promise<void> {
  if (typeof window === 'undefined') return;

  const keys = await listKeys();
  if (keys.length === 0) {
    setState({ status: 'needs-credential', message: null });
    return;
  }
  if (isSignedOut()) {
    setState({ status: 'signed-out', message: null });
    return;
  }

  const active = await activeSummary();
  if (active && hasEntered(active.protection)) {
    try {
      key = await resumeActiveKey();
      await connect();
      return;
    } catch {
      // Fall through to the key screen rather than dying on a stale record.
      key = null;
    }
  }

  setState({ status: 'locked', message: null });
}

/**
 * The explicit "Войти": unlocks the key - prompting for Windows Hello when the
 * key is protected - and only then opens the socket.
 */
export async function signIn(): Promise<void> {
  setState({ status: 'authenticating', message: null });
  try {
    key = await unlockActiveKey();
  } catch (err) {
    key = null;
    setState({ status: 'locked', message: (err as Error).message });
    throw err;
  }
  clearSignedOut();
  await connect();
}

/** Opens the socket with an already unlocked key. */
export async function connect(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  key ??= currentUnlocked();
  if (!key) {
    // Nothing unlocked: fall back to the screen that asks a human.
    await bootstrap();
    return;
  }

  closedByUs = false;
  setState({ status: 'connecting', attempt, reconnectInMs: null });

  socket = new WebSocket(wsUrl());
  socket.onopen = handleOpen;
  socket.onmessage = handleMessage;
  socket.onclose = handleClose;
  socket.onerror = () => {
    // onclose always follows; the error itself carries nothing useful.
  };
}

export function disconnect(): void {
  closedByUs = true;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  socket?.close(1000, 'client');
  socket = null;
}

function handleOpen(): void {
  if (!key) return;
  attempt = 0;
  setState({ status: 'authenticating', attempt: 0 });

  const resume: Record<string, number> = {};
  for (const [topic, seq] of lastSeq) resume[topic] = seq;

  sendFrame({
    t: 'hello',
    v: PROTOCOL_VERSION,
    kid: key.kid,
    install: key.install,
    ...(Object.keys(resume).length ? { resume } : {}),
  });
}

async function handleMessage(event: MessageEvent<string>): Promise<void> {
  let frame: ServerFrame;
  try {
    frame = JSON.parse(event.data) as ServerFrame;
  } catch {
    return;
  }

  switch (frame.t) {
    case 'ping':
      sendFrame({ t: 'pong', ts: frame.ts });
      return;

    case 'challenge': {
      if (!key) return;
      const cnonce = randomBase64Url(16);
      const payload = signaturePayload({
        prefix: SIGNATURE_PREFIX,
        // The app named in the credential, not location.hostname: the signature
        // must mean the same thing regardless of how the page was reached.
        issuer: key.app,
        kid: key.kid,
        nonce: frame.nonce,
        ts: frame.ts,
        cnonce,
      });
      const sig = await signPayload(key.key, payload);
      sendFrame({ t: 'auth', kid: key.kid, sig, cnonce });
      return;
    }

    case 'auth.ok': {
      // Only now is it certain this key works, so this is where a resumable
      // session begins.
      markEntered(key?.protection ?? 'none');
      setState({
        status: 'ready',
        owner: frame.owner,
        kid: frame.kid,
        label: frame.label,
        message: null,
        rejectedKid: null,
        reconnectInMs: null,
      });
      // Only makes the first SSR document non-empty. Never authenticates the
      // socket, which always re-signs a fresh challenge.
      const maxAge = frame.expDays * 24 * 3600;
      document.cookie =
        `${SSR_COOKIE}=${encodeURIComponent(frame.ssrToken)}; Path=/; SameSite=Strict; ` +
        `Max-Age=${maxAge}${location.protocol === 'https:' ? '; Secure' : ''}`;
      for (const topic of topics.keys()) sendSubscribe(topic);
      void onAuthenticated?.();
      return;
    }

    case 'auth.err': {
      const needsCredential = frame.code === 'not_ready';
      // A key the server refuses must not be resumed on the next reload.
      clearEntered();
      setState({
        status: needsCredential ? 'needs-credential' : 'auth-failed',
        message: needsCredential ? null : frame.msg,
        // Name the key that was refused: with several stored, "ключ отклонён"
        // without saying which one is not actionable.
        rejectedKid: needsCredential ? null : (key?.kid ?? null),
      });
      closedByUs = true; // do not spin: the key will not become valid by retrying
      socket?.close(1000, frame.code);
      return;
    }

    case 'res': {
      const p = pending.get(frame.id);
      if (!p) return;
      pending.delete(frame.id);
      clearTimeout(p.timer);
      if (frame.ok) p.resolve(frame.d);
      else p.reject(frame.e);
      return;
    }

    case 'event': {
      lastSeq.set(frame.topic, frame.seq);
      const listeners = topics.get(frame.topic);
      if (listeners) for (const l of listeners) l(frame.seq, frame.d);
      return;
    }

    case 'bye': {
      closedByUs = frame.code === 'revoked';
      setState({
        status: frame.code === 'revoked' ? 'auth-failed' : 'offline',
        message: frame.msg ?? frame.code,
      });
      return;
    }

    case 'dev.reload':
      location.reload();
      return;
  }
}

function handleClose(): void {
  socket = null;
  failAllPending(protocolError('offline', 'соединение разорвано'));

  if (closedByUs) return;

  attempt += 1;
  const delay = Math.min(RECONNECT_MIN_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
  const jittered = Math.round(delay * (0.7 + Math.random() * 0.6));

  setState({ status: 'offline', reconnectInMs: jittered, attempt });
  reconnectTimer = setTimeout(() => void connect(), jittered);
}

// ── requests ───────────────────────────────────────────────────────────────

/**
 * Спросить у человека код второго фактора.
 *
 * Ставится один раз при запуске приложения. Транспорт не знает, как выглядит
 * окно, а окно не знает про повторный вызов — и правильно: иначе каждый экран
 * с опасной кнопкой обзавёлся бы своей копией этой логики.
 */
let askOtp: ((method: MethodName) => Promise<string | null>) | null = null;

export function onOtpNeeded(ask: (method: MethodName) => Promise<string | null>): void {
  askOtp = ask;
}

/**
 * Отказ именно из-за второго фактора, а не любой `forbidden`.
 *
 * Смотрим `msg`, а не `message`. Отказ протокола — это объект `{code, msg}`, и
 * первая редакция читала поле, которого там нет: проверка молча возвращала
 * «нет» на каждый отказ, окно кода не открывалось никогда, а владелец видел
 * красную строку с просьбой добавить поле otp — которое добавлять некуда,
 * потому что добавляет его как раз это окно.
 */
function needsOtp(err: unknown): boolean {
  const e = err as { code?: string; msg?: string; message?: string } | null;
  const text = e?.msg ?? e?.message ?? '';
  return e?.code === 'forbidden' && text.includes('второго фактора');
}

/**
 * Вызов метода. При отказе «нужен код» спрашивает код и повторяет — один раз.
 *
 * Повтор живёт ЗДЕСЬ по той же причине, по которой проверка на сервере живёт в
 * одном месте: опасных действий больше десятка, и просить код в каждом
 * обработчике кнопки значило бы однажды забыть про одну кнопку.
 */
export async function request<T = unknown>(method: MethodName, payload?: unknown): Promise<T> {
  try {
    return await call<T>(method, payload);
  } catch (err) {
    if (!needsOtp(err) || !askOtp) throw err;
    const code = await askOtp(method);
    if (!code) throw err;
    return call<T>(method, { ...(payload as object ?? {}), otp: code });
  }
}

function call<T = unknown>(method: MethodName, payload?: unknown): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (socket?.readyState !== WebSocket.OPEN) {
      reject(protocolError('offline', 'нет соединения с сервером'));
      return;
    }
    const id = nextId++;
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(protocolError('timeout', `метод ${method} не ответил вовремя`));
    }, REQUEST_TIMEOUT_MS);

    pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
    sendFrame({ t: 'req', id, m: method, ...(payload === undefined ? {} : { p: payload }) });
  });
}

// ── subscriptions ──────────────────────────────────────────────────────────

function sendSubscribe(topic: string): void {
  const since = lastSeq.get(topic);
  sendFrame({
    t: 'sub', id: nextId++, topic, ...(since === undefined ? {} : { since }),
  });
}

export function subscribe(topic: string, listener: TopicListener): () => void {
  let set = topics.get(topic);
  if (!set) {
    set = new Set();
    topics.set(topic, set);
    sendSubscribe(topic);
  }
  set.add(listener);

  return () => {
    const current = topics.get(topic);
    if (!current) return;
    current.delete(listener);
    if (current.size > 0) return;
    topics.delete(topic);
    sendFrame({ t: 'unsub', id: nextId++, topic });
  };
}

/** Called after importing, switching or deleting a key. */
export async function reconnectWithNewKey(): Promise<void> {
  disconnect();
  lastSeq.clear();
  attempt = 0;
  key = null;
  lock();
  setState({ rejectedKid: null, message: null });
  await bootstrap();
}

/** After an import the owner has just proved intent, so go straight in. */
export async function enterAfterImport(): Promise<void> {
  disconnect();
  lastSeq.clear();
  attempt = 0;
  key = null;
  lock();
  setState({ rejectedKid: null, message: null });
  await signIn();
}

/**
 * Leaves the console without forgetting anything: the key stays in IndexedDB,
 * so coming back is one click. Deleting a key is a separate, explicit action.
 */
export function signOut(): void {
  disconnect();
  lastSeq.clear();
  key = null;
  attempt = 0;
  // Both halves matter: forget the unlocked key for this page, and remember
  // across reloads that the owner asked to be let out.
  lock();
  markSignedOut();
  clearEntered();
  document.cookie =
    `${SSR_COOKIE}=; Path=/; SameSite=Strict; Max-Age=0` +
    (location.protocol === 'https:' ? '; Secure' : '');
  setState({
    status: 'signed-out',
    owner: null, kid: null, label: null,
    message: null, rejectedKid: null, reconnectInMs: null, attempt: 0,
  });
}
